/**
 * ReviewOutcomesPage — /review-outcomes (WS-5b / rubric N)
 *
 * Trust-phase analytics for the human-review loop. A time-window selector drives
 * a summary of total reviews plus a table grouped by (outputType, taskType)
 * showing approve/edit/reject counts, the approve-without-edit rate as a %, the
 * average quality score, and the earned trust phase. Each row also renders a
 * simple inline approve/edit/reject breakdown bar.
 *
 * Mirrors CostAnalysisPage (TimeWindowSelector + shared chart primitives) and
 * the Table layout of the sibling admin pages.
 */

import { useCallback, useEffect, useState, startTransition } from 'react';
import {
  getReviewOutcomeStats,
  type ReviewOutcomeStatsResponse,
  type ReviewOutcomeStatsDto,
  type TrustPhase,
} from '../api/orchestratorClient';
import { useLatestRequest } from '../hooks/useLatestRequest';
import { TimeWindowSelector, loadStoredWindow, type TimeWindow } from '../components/TimeWindowSelector';

// ---------------------------------------------------------------------------
// Trust phase badge
// ---------------------------------------------------------------------------

const TRUST_PHASE_CLASSES: Record<TrustPhase, string> = {
  suggestion: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  drafting: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  autonomy: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
};

function TrustPhaseBadge({ phase }: { phase: TrustPhase }) {
  const cls = TRUST_PHASE_CLASSES[phase] ?? 'bg-zinc-700/40 text-zinc-400 border-zinc-600/40';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium capitalize ${cls}`}>
      {phase}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Approve / edit / reject breakdown bar
// ---------------------------------------------------------------------------

const OUTCOME_COLORS = {
  approve: '#10b981',
  edit: '#f59e0b',
  reject: '#ef4444',
};

function OutcomeBar({ row }: { row: ReviewOutcomeStatsDto }) {
  const total = row.approveCount + row.editCount + row.rejectCount;
  if (total === 0) {
    return <span className="text-xs text-zinc-600 italic">—</span>;
  }
  const segments = [
    { key: 'approve', value: row.approveCount, color: OUTCOME_COLORS.approve },
    { key: 'edit', value: row.editCount, color: OUTCOME_COLORS.edit },
    { key: 'reject', value: row.rejectCount, color: OUTCOME_COLORS.reject },
  ];
  return (
    <div
      className="flex h-3 w-40 overflow-hidden rounded-full bg-zinc-800"
      role="img"
      aria-label={`Approve ${row.approveCount}, edit ${row.editCount}, reject ${row.rejectCount}`}
    >
      {segments.map((s) =>
        s.value > 0 ? (
          <div
            key={s.key}
            style={{ width: `${(s.value / total) * 100}%`, background: s.color }}
            title={`${s.key}: ${s.value}`}
          />
        ) : null,
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

function fmtPct(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

function fmtScore(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return v.toFixed(2);
}

function groupLabel(row: ReviewOutcomeStatsDto): { output: string; task: string } {
  return {
    output: row.outputType?.trim() || '—',
    task: row.taskType?.trim() || '—',
  };
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function ReviewOutcomesPage() {
  const [timeWindow, setTimeWindow] = useState<TimeWindow>(loadStoredWindow);
  const [data, setData] = useState<ReviewOutcomeStatsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const beginLoad = useLatestRequest();

  const fetchStats = useCallback(async () => {
    const isCurrent = beginLoad();
    setIsLoading(true);
    setError(null);
    try {
      const result = await getReviewOutcomeStats(timeWindow);
      if (!isCurrent()) return;
      setData(result);
    } catch (err: unknown) {
      if (!isCurrent()) return;
      setError(err instanceof Error ? err.message : 'Failed to load review outcomes');
    } finally {
      if (isCurrent()) setIsLoading(false);
    }
  }, [beginLoad, timeWindow]);

  useEffect(() => {
    startTransition(() => { void fetchStats(); });
  }, [fetchStats]);

  // Sort rows by volume (most-reviewed slices first).
  const rows = data ? [...data.items].sort((a, b) => b.totalReviews - a.totalReviews) : [];

  return (
    <main className="flex-1 flex flex-col min-h-0 overflow-y-auto px-6 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-sm font-semibold text-zinc-100">Review Outcomes</h1>
          <p className="text-xs text-zinc-500">Trust-phase analytics for the human-review loop</p>
        </div>
        <TimeWindowSelector value={timeWindow} onChange={setTimeWindow} />
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          { label: 'Total Reviews', value: (data?.totalReviews ?? 0).toLocaleString() },
          { label: 'Window', value: `${data?.windowDays ?? timeWindow} days` },
          { label: 'Grouped Slices', value: (data?.items.length ?? 0).toLocaleString() },
        ].map((tile) => (
          <div key={tile.label} className="p-4 rounded-xl border border-zinc-800 bg-zinc-900">
            <p className="text-xs text-zinc-400">{tile.label}</p>
            <p className="text-xl font-bold text-zinc-100 mt-1">{tile.value}</p>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mb-3 text-xs text-zinc-400">
        {(
          [
            { label: 'Approve', color: OUTCOME_COLORS.approve },
            { label: 'Edit', color: OUTCOME_COLORS.edit },
            { label: 'Reject', color: OUTCOME_COLORS.reject },
          ] as const
        ).map((l) => (
          <span key={l.label} className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full" style={{ background: l.color }} />
            {l.label}
          </span>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div
          role="alert"
          className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-xs text-red-300"
        >
          {error}
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-8 bg-zinc-800 rounded animate-pulse" />
          ))}
        </div>
      )}

      {/* Empty */}
      {!isLoading && !error && data && rows.length === 0 && (
        <div className="flex items-center justify-center h-40 text-zinc-500 text-sm">
          No review activity in this window.
        </div>
      )}

      {/* Table */}
      {!isLoading && !error && rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left border-collapse">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="px-3 py-2 text-zinc-400 font-medium uppercase tracking-wider">Output Type</th>
                <th className="px-3 py-2 text-zinc-400 font-medium uppercase tracking-wider">Task Type</th>
                <th className="px-3 py-2 text-zinc-400 font-medium uppercase tracking-wider">Reviews</th>
                <th className="px-3 py-2 text-zinc-400 font-medium uppercase tracking-wider">Breakdown</th>
                <th className="px-3 py-2 text-zinc-400 font-medium uppercase tracking-wider">Approve / Edit / Reject</th>
                <th className="px-3 py-2 text-zinc-400 font-medium uppercase tracking-wider">Approve w/o Edit</th>
                <th className="px-3 py-2 text-zinc-400 font-medium uppercase tracking-wider">Avg Quality</th>
                <th className="px-3 py-2 text-zinc-400 font-medium uppercase tracking-wider">Trust Phase</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const { output, task } = groupLabel(row);
                return (
                  <tr key={`${output}::${task}::${i}`} className="border-b border-zinc-800/50 hover:bg-zinc-800/40">
                    <td className="px-3 py-2 text-zinc-200 font-medium">{output}</td>
                    <td className="px-3 py-2 text-zinc-300">{task}</td>
                    <td className="px-3 py-2 text-zinc-300">{row.totalReviews.toLocaleString()}</td>
                    <td className="px-3 py-2"><OutcomeBar row={row} /></td>
                    <td className="px-3 py-2 text-zinc-400 font-mono">
                      {row.approveCount} / {row.editCount} / {row.rejectCount}
                    </td>
                    <td className="px-3 py-2 text-zinc-200 font-medium">{fmtPct(row.approveWithoutEditRate)}</td>
                    <td className="px-3 py-2 text-zinc-300">{fmtScore(row.averageQualityScore)}</td>
                    <td className="px-3 py-2"><TrustPhaseBadge phase={row.trustPhase} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
